import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  ClockCounterClockwise,
  Upload,
  Download,
  ArrowCounterClockwise,
  CircleNotch,
  FileText,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  fetchDocumentVersions,
  uploadDocumentVersion,
  restoreDocumentVersion,
  downloadDocument,
} from '@/lib/documentsApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  documentId: string;
  documentName: string;
  onClose: () => void;
}

function formatSize(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DocumentVersionsModal({ documentId, documentName, onClose }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const versionsQ = useQuery({
    queryKey: ['document-versions', documentId],
    queryFn: () => fetchDocumentVersions(documentId),
  });

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      return uploadDocumentVersion(documentId, file);
    },
    onSuccess: (doc) => {
      toast.success(`New version uploaded: v${doc.name}`);
      qc.invalidateQueries({ queryKey: ['document-versions', documentId] });
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Could not upload new version'));
    },
  });

  const restoreMut = useMutation({
    mutationFn: async (versionId: string) => {
      setRestoringId(versionId);
      return restoreDocumentVersion(versionId);
    },
    onSuccess: () => {
      toast.success('Version restored successfully');
      qc.invalidateQueries({ queryKey: ['document-versions', documentId] });
      qc.invalidateQueries({ queryKey: ['documents'] });
      setRestoringId(null);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Could not restore version'));
      setRestoringId(null);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMut.mutate(file);
      e.target.value = '';
    }
  };

  const handleDownload = async (versionId: string) => {
    try {
      // Download through the AUTHENTICATED, integrity-verified /stream path — the
      // same as the main document download — not a presigned URL. Every byte is
      // re-hashed against its upload baseline before it reaches the browser.
      await downloadDocument(versionId);
    } catch {
      toast.error('Could not download this version');
    }
  };

  const versions = versionsQ.data ?? [];
  const isLoading = versionsQ.isLoading;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex max-h-full w-full max-w-lg flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <ClockCounterClockwise size={15} weight="duotone" className="text-brand-600 dark:text-brand-400" />
              Version history
            </h2>
            <p className="mt-0.5 max-w-[300px] truncate text-xs text-muted-foreground">
              {documentName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <CircleNotch size={24} className="animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && versions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <FileText size={32} className="mb-2 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No version history available</p>
              <p className="mt-1 text-xs text-muted-foreground/70">
                Upload a new version to start tracking changes
              </p>
            </div>
          )}

          {versions.length > 0 && (
            <div className="space-y-2">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className={`flex items-center gap-3 rounded-lg border p-3 transition ${
                    v.isLatest
                      ? 'border-brand-200 bg-brand-50/50 dark:border-brand-500/30 dark:bg-brand-900/20'
                      : 'border-border bg-card hover:bg-muted/30'
                  }`}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <span className="text-xs font-bold">v{v.version}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {v.name}
                      </span>
                      {v.isLatest && (
                        <Badge variant="default" className="h-4 px-1.5 text-[9px]">
                          Current
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{formatSize(v.fileSizeBytes)}</span>
                      <span>·</span>
                      <span>
                        {v.uploadedBy
                          ? `${v.uploadedBy.firstName} ${v.uploadedBy.lastName}`
                          : 'Unknown'}
                      </span>
                      <span>·</span>
                      <span>{formatDate(v.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => handleDownload(v.id)}
                      title="Download this version"
                    >
                      <Download size={14} />
                    </Button>
                    {!v.isLatest && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => restoreMut.mutate(v.id)}
                        disabled={restoreMut.isPending}
                        title="Restore this version"
                      >
                        {restoringId === v.id ? (
                          <CircleNotch size={14} className="animate-spin" />
                        ) : (
                          <ArrowCounterClockwise size={14} />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          <span className="text-xs text-muted-foreground">
            {versions.length} version{versions.length !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMut.isPending}
              className="gap-1.5"
            >
              {uploadMut.isPending ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : (
                <Upload size={14} />
              )}
              Upload new version
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      </Card>
    </div>
  );
}
